import {
  CONTACT_HURT,
  CONTACT_STOMP,
  PIT_RESCUE,
  STOMP_BOUNCE,
  STOMPABLE,
  contact,
  copyRunner,
  emptyBox,
  fellIn,
  stepRunner,
  supportAt,
  scrollShift,
  thingBox,
  type Box,
  type Metrics,
  type Pickup,
  type Pit,
  type Runner,
  type Thing,
  type ThingKind,
} from "./vineWorld";

// Vine Runner's demo bot. It plays the next second or so forward in its head
// for every jump time it could pick (with a double jump, and a jump again after
// landing), using the same physics and hit rules as the game with a little
// extra caution, then presses a third of the way into the first safe window.
// Pressing well inside the window is what makes it look calm, and it is also
// why a late copy of it (the playtest's sloppy player) survives only some of
// the time.

const SIM_DT = 1 / 30;
const MAX_STEPS = 56;
const MAX_THINGS = 28;
const JUMP_OPTIONS = 22;
const DOUBLE_AFTER = [-1, 4, 6, 8, 10, 13, 16];
const NEVER_STEPS = 30;
const LANDED_STEPS = 6;
// After a plan lands, the record must stay safe this long on the ground, or
// survive one of the follow-up jumps (steps after landing, then a double).
const FOLLOW_STEPS = 24;
const FOLLOW_UPS: Array<[number, number]> = [
  [1, -1],
  [1, 8],
  [4, -1],
  [4, 8],
  [8, 8],
  [2, 12],
];
const SAFE = 1e6;
// Where in a safe window the bot presses: a little early, so a late press
// still lands inside it.
const AIM = 0.35;

export interface PlanWorld {
  m: Metrics;
  things: Thing[];
  pits: Pit[];
  pickups: Pickup[];
  scroll: number;
  scrollTarget: number;
  runner: Runner;
  invulnerable: number;
}

export type BotAction = "none" | "jump";

export function createPlanner() {
  const boxes: Box[][] = Array.from({ length: MAX_STEPS }, () => Array.from({ length: MAX_THINGS }, emptyBox));
  const kinds: ThingKind[] = new Array(MAX_THINGS).fill("bar");
  const tops = Array.from({ length: MAX_STEPS }, () => new Float64Array(3));
  const topCounts = new Int32Array(MAX_STEPS);
  const groundOk = new Uint8Array(MAX_STEPS);
  const stomped = new Uint8Array(MAX_THINGS);
  const values = new Float64Array(JUMP_OPTIONS);
  const probe = emptyBox();
  const shifts = new Float64Array(MAX_STEPS);
  const sim: Runner = { alt: 0, vUp: 0, jumps: 0, grounded: true, air: 0 };
  let count = 0;
  let valuable = false;
  let world: PlanWorld | null = null;
  const bonus: Pickup[] = [];

  // Where everything near the record will be at each step of the look-ahead.
  function prepare(input: PlanWorld) {
    world = input;
    const { m, scroll, scrollTarget } = input;
    for (let s = 0; s < MAX_STEPS; s++) shifts[s] = scrollShift(scroll, scrollTarget, (s + 1) * SIM_DT);
    const reach = m.px + (scroll * 1.6 + m.width) * SIM_DT * MAX_STEPS;
    count = 0;
    valuable = false;
    for (const thing of input.things) {
      if (count >= MAX_THINGS) break;
      if (!thingBox(thing, 0, 0, probe)) {
        // A bomb high above is not live yet but will be.
        if (thing.kind !== "beam" && thing.kind !== "boss") continue;
      }
      const left = probe.round ? probe.cx - probe.rad : probe.x0;
      const right = probe.round ? probe.cx + probe.rad : probe.x1;
      if (right < m.px - m.r * 3 || left > reach) continue;
      kinds[count] = thing.kind;
      if (STOMPABLE[thing.kind]) valuable = true;
      for (let s = 0; s < MAX_STEPS; s++) thingBox(thing, (s + 1) * SIM_DT, shifts[s], boxes[s][count]);
      count++;
    }
    bonus.length = 0;
    for (const pickup of input.pickups) {
      if (pickup.taken || pickup.kind === "note") continue;
      if (pickup.x < m.px - m.r * 2 || pickup.x > reach) continue;
      bonus.push(pickup);
      valuable = true;
    }
    for (let s = 0; s < MAX_STEPS; s++) {
      const support = supportAt(m.px, m.r, boxes[s], kinds, count, input.pits, shifts[s], tops[s], 0.2);
      topCounts[s] = support.topCount;
      groundOk[s] = support.groundOk ? 1 : 0;
    }
  }

  // Plays one plan forward: a press at `jumpAt`, another `doubleAfter` steps
  // later, and optionally a follow-up (`follow`, an index into FOLLOW_UPS)
  // once the record is back on the ground. Returns SAFE plus what it earned,
  // or the step at which it gets hurt (later is better). `landedAt` is left
  // at the step it first landed, or -1.
  let landedAt = -1;
  function run(jumpAt: number, doubleAfter: number, follow = -1) {
    const input = world!;
    const { m } = input;
    copyRunner(input.runner, sim);
    stomped.fill(0);
    let stomps = 0;
    let bossHits = 0;
    let picks = 0;
    let pressA = jumpAt;
    let pressB = jumpAt >= 0 && doubleAfter >= 0 ? jumpAt + doubleAfter : -1;
    const margin = m.r * 0.1;
    let flown = !sim.grounded;
    let groundedFor = 0;
    let landings = 0;
    landedAt = -1;
    for (let s = 0; s < MAX_STEPS; s++) {
      const pending = s <= pressA || s <= pressB;
      if (!pending) {
        // Without a follow-up the record has to stay safe on the ground for
        // a while; with one, until it lands from the follow-up.
        if (!flown && s >= NEVER_STEPS) break;
        if (follow < 0 && landings > 0 && groundedFor >= FOLLOW_STEPS) break;
        if (follow >= 0 && landings > 1 && groundedFor >= LANDED_STEPS) break;
      }
      const prevAlt = sim.alt;
      const wasGrounded = sim.grounded;
      stepRunner(sim, SIM_DT, m, s === pressA || s === pressB, tops[s], topCounts[s], groundOk[s] === 1);
      if (sim.grounded) {
        groundedFor++;
        if (!wasGrounded && flown) {
          landings++;
          if (landings === 1) {
            landedAt = s;
            if (follow >= 0) {
              const [first, second] = FOLLOW_UPS[follow];
              pressA = s + first;
              pressB = second >= 0 ? s + first + second : -1;
            }
          }
        }
      } else {
        flown = true;
        groundedFor = 0;
      }
      const tau = (s + 1) * SIM_DT;
      const shielded = tau < input.invulnerable;
      if (fellIn(sim, m)) {
        if (!shielded) return s;
        sim.vUp = m.v0 * PIT_RESCUE;
      }
      const row = boxes[s];
      for (let i = 0; i < count; i++) {
        if (stomped[i]) continue;
        const hit = contact(kinds[i], row[i], m.px, sim.alt, prevAlt, sim.vUp, m.r, margin, 0.3);
        if (hit === CONTACT_STOMP) {
          stomped[i] = 1;
          if (kinds[i] === "boss") bossHits++;
          else stomps++;
          sim.vUp = m.v0 * STOMP_BOUNCE;
          sim.jumps = 1;
          sim.grounded = false;
        } else if (hit === CONTACT_HURT && !shielded) {
          return s;
        }
      }
      for (const pickup of bonus) {
        const dx = pickup.x - shifts[s] - m.px;
        const da = pickup.alt - sim.alt;
        if (dx * dx + da * da < m.r * m.r * 4) {
          picks = 1;
        }
      }
    }
    return SAFE + bossHits * 1e4 + stomps * 100 + picks * 10;
  }

  // A plan that gets hurt only after it has landed may still be fine if the
  // bot jumps again from there, so try the usual follow-ups before giving up.
  function plan(jumpAt: number, doubleAfter: number) {
    const plain = run(jumpAt, doubleAfter);
    if (plain >= SAFE || landedAt < 0 || plain <= landedAt) return plain;
    let best = plain;
    for (let f = 0; f < FOLLOW_UPS.length; f++) {
      const value = run(jumpAt, doubleAfter, f);
      if (value > best) best = value;
      if (best >= SAFE) break;
    }
    return best;
  }

  // Fills `values` with the best outcome for a press at each step from now.
  // Returns the outcome of never pressing.
  function evaluate(canDouble: boolean) {
    const never = plan(-1, -1);
    if (never >= SAFE && !valuable) return { never, any: false };
    for (let k = 0; k < JUMP_OPTIONS; k++) {
      let best = plan(k, -1);
      if (canDouble && (best < SAFE || valuable)) {
        for (let d = 1; d < DOUBLE_AFTER.length; d++) {
          const value = plan(k, DOUBLE_AFTER[d]);
          if (value > best) best = value;
        }
      }
      values[k] = best;
    }
    return { never, any: true };
  }

  // Remembered start of the current window, in game seconds.
  let windowStart: number | null = null;
  let windowKey = "";

  return {
    decide(input: PlanWorld, now: number): BotAction {
      const { runner } = input;
      const key = `${runner.jumps}:${runner.grounded ? 1 : 0}`;
      if (key !== windowKey) {
        windowKey = key;
        windowStart = null;
      }
      if (runner.jumps >= 2) return "none";
      prepare(input);
      const onGround = runner.grounded || runner.jumps === 0;
      const { never, any } = evaluate(onGround);
      if (!any) {
        windowStart = null;
        return "none";
      }
      let best = -Infinity;
      for (let k = 0; k < JUMP_OPTIONS; k++) if (values[k] > best) best = values[k];
      if (never >= best) {
        windowStart = null;
        return "none";
      }
      let first = 0;
      while (first < JUMP_OPTIONS && values[first] !== best) first++;
      let last = first;
      while (last + 1 < JUMP_OPTIONS && values[last + 1] === best) last++;
      if (first > 0) {
        windowStart = now + first * SIM_DT;
        return "none";
      }
      const start = windowStart !== null && windowStart <= now + 1e-6 && windowStart > now - 1.5 ? windowStart : now;
      windowStart = start;
      // A window that runs past the look-ahead: no rush yet.
      if (last === JUMP_OPTIONS - 1 && best >= SAFE) return "none";
      const end = now + last * SIM_DT;
      if (last <= 1 || now >= start + (end - start) * AIM - SIM_DT / 2) {
        windowStart = null;
        return "jump";
      }
      return "none";
    },
  };
}
